import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  ACCENT_PRESET_KEYS,
  DEFAULT_ACCENT_PRESET,
  DEFAULT_FONT_FAMILY,
  DEFAULT_FONT_SCALE,
  DEFAULT_LINE_HEIGHT,
  FONT_SCALE_OPTIONS,
  FONT_FAMILY_OPTIONS,
  LINE_HEIGHT_OPTIONS,
  getAccentPreset,
  type AccentPresetKey,
  type FontFamilyKey,
  type FontScaleKey,
  type LineHeightKey,
} from '../lib/themePresets';
import { paletteFromAccent } from '../lib/paletteFromAccent';

type ThemePref = 'system' | 'light' | 'dark';
type MotionPref = 'system' | 'reduce';

interface ThemeContextValue {
  theme: ThemePref;
  motion: MotionPref;
  accent: AccentPresetKey;
  fontFamily: FontFamilyKey;
  fontScale: FontScaleKey;
  lineHeight: LineHeightKey;
  resolvedTheme: 'light' | 'dark';
  setTheme: (t: ThemePref) => void;
  setMotion: (m: MotionPref) => void;
  setAccent: (a: AccentPresetKey) => void;
  setFontFamily: (f: FontFamilyKey) => void;
  setFontScale: (s: FontScaleKey) => void;
  setLineHeight: (l: LineHeightKey) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const THEME_KEY = '7oh.theme';
const MOTION_KEY = '7oh.motion';
const ACCENT_KEY = '7oh.accent';
const FONT_FAMILY_KEY = '7oh.fontFamily';
const FONT_SCALE_KEY = '7oh.fontScale';
const LINE_HEIGHT_KEY = '7oh.lineHeight';

function readStored<T extends string>(key: string, fallback: T, allowed?: readonly T[]): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    if (allowed && !allowed.includes(raw as T)) return fallback;
    return raw as T;
  } catch {
    return fallback;
  }
}

function resolveTheme(pref: ThemePref): 'light' | 'dark' {
  if (pref === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return pref;
}

const FONT_FAMILY_KEYS = FONT_FAMILY_OPTIONS.map((o) => o.key) as readonly FontFamilyKey[];
const FONT_SCALE_KEYS = FONT_SCALE_OPTIONS.map((o) => o.key) as readonly FontScaleKey[];
const LINE_HEIGHT_KEYS = LINE_HEIGHT_OPTIONS.map((o) => o.key) as readonly LineHeightKey[];

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemePref>(() =>
    readStored<ThemePref>(THEME_KEY, 'system', ['system', 'light', 'dark']),
  );
  const [motion, setMotionState] = useState<MotionPref>(() =>
    readStored<MotionPref>(MOTION_KEY, 'system', ['system', 'reduce']),
  );
  const [accent, setAccentState] = useState<AccentPresetKey>(() =>
    readStored<AccentPresetKey>(ACCENT_KEY, DEFAULT_ACCENT_PRESET, ACCENT_PRESET_KEYS),
  );
  const [fontFamily, setFontFamilyState] = useState<FontFamilyKey>(() =>
    readStored<FontFamilyKey>(FONT_FAMILY_KEY, DEFAULT_FONT_FAMILY, FONT_FAMILY_KEYS),
  );
  const [fontScale, setFontScaleState] = useState<FontScaleKey>(() =>
    readStored<FontScaleKey>(FONT_SCALE_KEY, DEFAULT_FONT_SCALE, FONT_SCALE_KEYS),
  );
  const [lineHeight, setLineHeightState] = useState<LineHeightKey>(() =>
    readStored<LineHeightKey>(LINE_HEIGHT_KEY, DEFAULT_LINE_HEIGHT, LINE_HEIGHT_KEYS),
  );
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => resolveTheme(theme));

  // Apply theme to <html data-theme>.
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', theme);
    }
    setResolvedTheme(resolveTheme(theme));
  }, [theme]);

  // Sync browser/PWA chrome (status bar, address bar tint) with the in-app
  // theme toggle. index.html ships a single bare <meta name="theme-color"> as
  // the pre-JS default; we overwrite its `content` here so the chrome follows
  // the user's explicit choice rather than the OS `prefers-color-scheme`.
  // Values mirror `--color-bg-canvas` in src/styles/tokens.css.
  useEffect(() => {
    let meta = document.querySelector(
      'meta[name="theme-color"]:not([media])',
    ) as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      document.head.appendChild(meta);
    }
    // Light: hsl(36 35% 94%) — warm paper canvas
    // Dark:  hsl(280 12% 8%) — deep aubergine-black canvas
    meta.setAttribute('content', resolvedTheme === 'dark' ? '#151217' : '#f5f1ea');
  }, [resolvedTheme]);

  // React to OS theme changes when in system mode.
  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setResolvedTheme(mq.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  // Motion: applies a data attribute the CSS can hook to force reduced-motion.
  useEffect(() => {
    document.documentElement.setAttribute('data-motion', motion);
  }, [motion]);

  // Phase 10A — accent palette. Set the 4 accent custom properties
  // directly on <html>; inline runtime properties outweigh the stylesheet,
  // so this overrides both :root and :root[data-theme='dark']. Re-runs
  // when accent OR resolvedTheme changes so swapping light↔dark picks
  // the right preset triplet.
  useEffect(() => {
    const preset = getAccentPreset(accent);
    const seed = resolvedTheme === 'dark' ? preset.dark : preset.light;
    const tokens = paletteFromAccent(seed, resolvedTheme);
    const root = document.documentElement;
    for (const [k, v] of Object.entries(tokens)) {
      root.style.setProperty(k, v);
    }
    // Mark the preset on the html element for analytics + e2e selectors.
    // Doesn't drive styling — the inline properties do.
    root.setAttribute('data-accent', accent);
  }, [accent, resolvedTheme]);

  // Phase 10A — typography. Three independent attrs the stylesheet
  // hooks into. font-scale is also pushed as an inline custom property
  // because the type-scale clamps are baseline-relative.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-font', fontFamily);
    root.setAttribute('data-font-scale', fontScale);
    root.setAttribute('data-leading', lineHeight);
  }, [fontFamily, fontScale, lineHeight]);

  const setTheme = (t: ThemePref) => {
    setThemeState(t);
    try { localStorage.setItem(THEME_KEY, t); } catch { /* noop */ }
  };
  const setMotion = (m: MotionPref) => {
    setMotionState(m);
    try { localStorage.setItem(MOTION_KEY, m); } catch { /* noop */ }
  };
  const setAccent = (a: AccentPresetKey) => {
    setAccentState(a);
    try { localStorage.setItem(ACCENT_KEY, a); } catch { /* noop */ }
  };
  const setFontFamily = (f: FontFamilyKey) => {
    setFontFamilyState(f);
    try { localStorage.setItem(FONT_FAMILY_KEY, f); } catch { /* noop */ }
  };
  const setFontScale = (s: FontScaleKey) => {
    setFontScaleState(s);
    try { localStorage.setItem(FONT_SCALE_KEY, s); } catch { /* noop */ }
  };
  const setLineHeight = (l: LineHeightKey) => {
    setLineHeightState(l);
    try { localStorage.setItem(LINE_HEIGHT_KEY, l); } catch { /* noop */ }
  };

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      motion,
      accent,
      fontFamily,
      fontScale,
      lineHeight,
      resolvedTheme,
      setTheme,
      setMotion,
      setAccent,
      setFontFamily,
      setFontScale,
      setLineHeight,
    }),
    [theme, motion, accent, fontFamily, fontScale, lineHeight, resolvedTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
